/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { type CreateRoleInput, type Role, type RoleModuleAccess, rolesApi } from '../api/roles.js';

const ROLES_KEY = ['roles'] as const;

export function useRoles(enabled: boolean): UseQueryResult<readonly Role[]> {
  return useQuery({ queryKey: ROLES_KEY, queryFn: () => rolesApi.list(), enabled });
}

export function useCreateRole(): UseMutationResult<{ id: string }, Error, CreateRoleInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => rolesApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useUpdateRoleAccess(): UseMutationResult<
  void,
  Error,
  { id: string; modules: readonly RoleModuleAccess[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, modules }: { id: string; modules: readonly RoleModuleAccess[] }) =>
      rolesApi.updateAccess(id, modules),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}
